// Dodo Payments overlay loader. Lazy-imports `dodopayments-checkout` and
// initializes once. Exposes the same `loadDodo` / `openCheckout` shape the
// app's billing flows expect.

import { DodoPayments, type CheckoutEvent } from "dodopayments-checkout";

let loadPromise: Promise<void> | null = null;

type EventListener = (ev: CheckoutEvent) => void;
const listeners = new Set<EventListener>();

export function loadDodo(): Promise<void> {
  if (loadPromise) return loadPromise;
  const mode: "live" | "test" =
    __VITE_DODO_ENVIRONMENT__ === "live_mode" ? "live" : "test";
  loadPromise = new Promise((resolve) => {
    DodoPayments.Initialize({
      mode,
      displayType: "overlay",
      onEvent: (ev) => {
        for (const fn of listeners) fn(ev);
      },
    });
    resolve();
  });
  return loadPromise;
}

/// Opens Dodo's overlay checkout at `checkoutUrl` and resolves once the user
/// completes payment (Dodo fires `checkout.redirect` when payment succeeds and
/// it would otherwise redirect - with `manualRedirect: true` we intercept it
/// and call the server's verify-active endpoint instead). Rejects if the user
/// closes the overlay, the link expires, or an error event fires.
export async function openCheckout(checkoutUrl: string): Promise<void> {
  await loadDodo();
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (cb: () => void) => {
      if (settled) return;
      settled = true;
      listeners.delete(listener);
      try {
        DodoPayments.Checkout.close();
      } catch {
        // already closed
      }
      cb();
    };
    const listener: EventListener = (ev) => {
      switch (ev.event_type) {
        case "checkout.redirect":
        case "checkout.redirect_requested":
          finish(() => resolve());
          break;
        case "checkout.status": {
          // Dodo sometimes finishes the flow without a redirect event when
          // there is no `return_url`; the success state arrives here.
          const status =
            (ev.data?.status as string | undefined) ??
            ((ev.data?.message as Record<string, unknown> | undefined)
              ?.status as string | undefined);
          if (
            status &&
            ["succeeded", "success", "paid", "completed", "active"].includes(
              status.toLowerCase(),
            )
          ) {
            finish(() => resolve());
          }
          break;
        }
        case "checkout.closed":
          finish(() => reject(new Error("checkout closed before completion")));
          break;
        case "checkout.error":
          finish(() =>
            reject(
              new Error(
                `checkout error: ${
                  (ev.data?.message as string | undefined) ?? "unknown"
                }`,
              ),
            ),
          );
          break;
        case "checkout.link_expired":
          finish(() => reject(new Error("checkout link expired")));
          break;
      }
    };
    listeners.add(listener);
    DodoPayments.Checkout.open({
      checkoutUrl,
      options: { manualRedirect: true },
    });
  });
}
